import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ListboxComponent, ListboxConfig, ListboxItem, ListboxTab } from '@groundworks/ngx-groundworks-ui';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../config.service';
import { BranchInfo, PendingSaleInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';
import { DefaultBranchService } from '../../services/default-branch.service';
import { SaleApprovalService } from '../../services/sale-approval.service';
import { SaleApprovalsSignalRService } from '../../services/sale-approvals-signalr.service';
import { mapPendingSaleToListboxItem } from '../../utils/listbox.util';
import { FilterOptions, SideModalComponent } from '../side-modal/side-modal.component';

@Component({
  selector: 'app-sale-approval-listbox',
  imports: [ListboxComponent, SideModalComponent],
  templateUrl: './sale-approval-listbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaleApprovalListboxComponent implements OnInit, OnDestroy {
  @ViewChild('sideModal') sideModalComponent!: SideModalComponent;
  private readonly configService = inject(ConfigService);
  private readonly saleApprovalService = inject(SaleApprovalService);
  private readonly branchService = inject(BranchService);
  private readonly defaultBranchService = inject(DefaultBranchService);
  private readonly saleApprovalsSignalRService = inject(SaleApprovalsSignalRService);
  private readonly destroy$ = new Subject<void>();
  private readonly removedOpportunityIds = new Set<string>();

  selectedBranches = signal<BranchInfo[]>([]);
  needsReviewSales: ListboxItem[] = [];
  serviceSalesToReview: ListboxItem[] = [];
  reviewedSales: ListboxItem[] = [];

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  showSettings = signal(true);
  hasLoadedData = signal(false);

  startDate = signal<Date | undefined>(undefined);
  endDate = signal<Date | undefined>(undefined);

  tableConfig = this.buildTableConfig('CFI');
  serviceTableConfig = this.buildTableConfig('Service Tech');
  reviewedTableConfig = this.buildTableConfig('CFI/Service Tech', true);

  private buildTableConfig(repColumnHeader: string, includeAppointmentType = false): ListboxConfig {
    return {
      columns: [
        { header: 'Branch', field: 'branch', sortable: true },
        { header: 'Customer Name', field: 'name', sortable: true },
        ...(includeAppointmentType
          ? [{ header: 'Appointment Type', field: 'appointmentType', sortable: true }]
          : []),
        { header: 'Sale Date', field: 'saleDate', sortable: true, dataType: 'date' },
        { header: 'Sale Amount', field: 'saleAmount', sortable: true, dataType: 'currency' },
        { header: repColumnHeader, field: 'cfiName', sortable: true },
        { header: 'Action', field: 'action', sortable: false },
      ],
    };
  }

  tabs = signal<ListboxTab[]>([]);

  readonly noDataMessage = computed(() => {
    if (this.selectedBranches().length === 0 || !this.hasLoadedData()) {
      return 'Please select a branch';
    }
    return 'No records found';
  });

  private initialLoadHasHappened = false;
  private hasDefaultBranch = signal(false);

  ngOnInit(): void {
    this.defaultBranchService.defaultBranch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasDefault) => {
        this.hasDefaultBranch.set(hasDefault);
        if (hasDefault && this.selectedBranches().length > 0 && !this.initialLoadHasHappened) {
          this.loadSaleApprovals();
          this.initialLoadHasHappened = true;
        }
      });

    this.branchService
      .getSelectedBranches()
      .pipe(takeUntil(this.destroy$))
      .subscribe((branches) => this.handleBranchSelectionChange(branches));

    this.saleApprovalsSignalRService.startConnection();
    this.saleApprovalsSignalRService.saleApproved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId) => {
        this.removeSaleFromNeedsReview(opportunityId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.saleApprovalsSignalRService.stopConnection();
  }

  private loadSaleApprovals(): void {
    const branches = this.selectedBranches();
    if (branches.length === 0) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.saleApprovalService
      .getUnreviewedSales(
        branches.map(branch => branch.id!),
        this.startDate(),
        this.endDate()
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          // Sort by sale date (newest first)
          const sortedNeedsReview = (data.needsReview ?? []).sort((a, b) => {
            const dateA = new Date(a.saleDate!).getTime();
            const dateB = new Date(b.saleDate!).getTime();
            return dateB - dateA;
          });

          const sortedReviewed = (data.reviewed ?? []).sort((a, b) => {
            const dateA = new Date(a.saleDate!).getTime();
            const dateB = new Date(b.saleDate!).getTime();
            return dateB - dateA;
          });

          this.needsReviewSales = sortedNeedsReview.map(sale => this.toListboxItem(sale));

          const sortedServiceNeedsReview = (data.serviceNeedsReview ?? []).sort((a, b) => {
            const dateA = new Date(a.saleDate!).getTime();
            const dateB = new Date(b.saleDate!).getTime();
            return dateB - dateA;
          });
          this.serviceSalesToReview = sortedServiceNeedsReview.map(sale => this.toListboxItem(sale));

          this.reviewedSales = sortedReviewed.map(sale => this.toListboxItem(sale));
          this.removedOpportunityIds.clear();
          this.updateTabs();
          this.isLoading.set(false);
          this.hasLoadedData.set(true);
        },
        error: (error) => {
          console.error('Error loading sale approvals:', error);
          this.errorMessage.set('Failed to load sale approvals');
          this.isLoading.set(false);
          this.hasLoadedData.set(true);
        },
      });
  }

  private toListboxItem(sale: PendingSaleInfo): ListboxItem {
    return mapPendingSaleToListboxItem(sale, this.configService.appBaseUrl);
  }

  private updateTabs(): void {
    this.tabs.set([
      {
        id: 1,
        label: 'Sales to Review',
        config: this.tableConfig,
        items: this.needsReviewSales
      },
      {
        id: 2,
        label: 'Service Sales to Review',
        config: this.serviceTableConfig,
        items: this.serviceSalesToReview
      },
      {
        id: 3,
        label: 'Reviewed',
        config: this.reviewedTableConfig,
        items: this.reviewedSales
      }
    ]);
  }

  private removeSaleFromNeedsReview(opportunityId: string): void {
    if (!opportunityId || this.removedOpportunityIds.has(opportunityId)) {
      console.warn('Sale already removed:', opportunityId);
      return;
    }

    const removedItem =
      this.spliceListboxItem(this.needsReviewSales, opportunityId) ??
      this.spliceListboxItem(this.serviceSalesToReview, opportunityId);

    if (removedItem) {
      this.removedOpportunityIds.add(opportunityId);
      this.reviewedSales.unshift(removedItem);
      this.updateTabs();
    }
  }

  private spliceListboxItem(list: ListboxItem[], id: string): ListboxItem | null {
    const index = list.findIndex(item => item.id === id);
    if (index === -1) return null;
    return list.splice(index, 1)[0];
  }

  private handleBranchSelectionChange(branches: BranchInfo[]): void {
    if (branches.length > 0) {
      this.selectedBranches.set(branches);
      this.hasLoadedData.set(false);

      if (!this.initialLoadHasHappened && this.hasDefaultBranch()) {
        this.loadSaleApprovals()
        this.initialLoadHasHappened = true;
      }
    } else {
      this.selectedBranches.set([]);
      this.hasLoadedData.set(false);
    }
  }

  handleAction(actionValue: string): void {
    window.open(actionValue, '_blank');
  }

  handleSettingsClick(): void {
    this.sideModalComponent.open(this.sideModalComponent.sideModalTemplate);
  }

  onFiltersChanged(filters?: FilterOptions): void {
    if (filters) {
      this.startDate.set(filters.startDate);
      this.endDate.set(filters.endDate);
    }
    this.loadSaleApprovals();
    this.initialLoadHasHappened = true;
  }
}
